import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  confirmLscNeonsportzUrlAndWait,
  getLscScheduleSource,
  listLscNeonsportzPublishedSeasons,
  previewLscNeonsportzUrl,
  LscApiError,
} from '../api';
import type {
  LscNeonsportzImportCounts,
  LscNeonsportzPublishedSeason,
  LscNeonsportzUrlConfirmResult,
  LscNeonsportzUrlPreviewResult,
  LscScheduleDiffSummary,
} from '../types';
import { displaySeasonLabel } from '../seasonDisplay';
import LscMutationErrorAlert from './LscMutationErrorAlert';
import {
  isLscEntitlementErrorCode,
  parseLscMutationError,
  type ParsedLscMutationError,
} from '../entitlementBillingErrors';
import {
  formatDiscordIdStatus,
  formatDiscordMemberLabel,
  formatNeonsportzImportError,
  formatTwitchLogin,
  NEONSPORTZ_IMPORT_ERROR_MESSAGES,
} from '../neonsportzImportUtils';
import {
  formatNeonsportzGameCounts,
  formatNeonsportzTeamCounts,
  formatScheduleWeekRange,
  formatTruncationNote,
  isNeonsportzScheduleIncomplete,
} from '../neonsportzCountUtils';
import { formatImportIssues } from '../importErrorUtils';
import { lscPath } from '../routing';
import { SCHEDULE_IMPORT_ROLLOVER_ACK_COPY, SCHEDULE_SYNC_TEAM_ASSIGNMENT_REVIEW_COPY } from '../setupScheduleCopy';
import ImportedMembersPromotionPanel from './ImportedMembersPromotionPanel';

function formatEditionDisplay(edition?: string | null): string | null {
  if (!edition) return null;
  const match = /^madden-(\d{2})$/i.exec(edition.trim());
  if (match) return `Madden ${match[1]}`;
  const cfb = /^cfb-(\d{2})$/i.exec(edition.trim());
  if (cfb) return `CFB ${cfb[1]}`;
  return edition;
}

export const NEONSPORTZ_EMPTY_SCHEDULE_COPY =
  "NeonSportz hasn't published a schedule for this season yet — check back once games are posted.";

function previewHasNoGames(counts?: LscNeonsportzImportCounts | null): boolean {
  if (!counts) return false;
  const fetched = counts.fetched_games ?? counts.games;
  const provider = counts.provider_total_games;
  if (fetched === 0 && (provider == null || provider === 0)) return true;
  if (provider === 0 && (fetched == null || fetched === 0)) return true;
  return false;
}

function formatSeasonWithEdition(
  season?: string | null,
  edition?: string | null,
  seasonIndex?: number | null
): string {
  const seasonLabel = displaySeasonLabel(season, seasonIndex, edition);
  const editionLabel = formatEditionDisplay(edition);
  if (seasonLabel && editionLabel) return `${seasonLabel} · ${editionLabel}`;
  return seasonLabel || editionLabel || '';
}

function editionRolloverAckCopy(diff?: LscScheduleDiffSummary | null): string {
  const incoming = formatEditionDisplay(diff?.incoming_edition) || 'Madden 27';
  if (!diff?.active_edition) {
    return (
      `This import is ${incoming}. Your league's earlier seasons don't have a Madden year on ` +
      'record. Confirm this is a new game year. Your earlier seasons and archives stay as they are.'
    );
  }
  const active = formatEditionDisplay(diff.active_edition) || 'the current edition';
  return `This starts a new ${incoming} season. Your ${active} seasons and archives stay as they are.`;
}

function teamAssignmentNextStepCopy(count: number): string {
  if (count === 1) return "1 member's team changed. Review before finishing.";
  if (count > 1) return `${count} members' teams changed. Review before finishing.`;
  return SCHEDULE_SYNC_TEAM_ASSIGNMENT_REVIEW_COPY;
}

interface NeonSportzUrlImportPanelProps {
  leagueId: string;
  onImportSuccess?: () => void;
}

export function DiffSummarySection({ diff }: { diff: LscScheduleDiffSummary }) {
  const counts = diff.counts || {};
  return (
    <div className="space-y-3" data-testid="neonsportz-diff-summary">
      <h4 className="font-medium text-white">Changes vs current schedule</h4>
      <ul className="grid gap-2 sm:grid-cols-2 text-sm text-secondary-300">
        <li>New matches: {counts.new_matches ?? 0}</li>
        <li>Updated matches: {counts.updated_matches ?? 0}</li>
        <li>Completed matches: {counts.completed_matches ?? 0}</li>
        <li>Removed/superseded: {counts.removed_or_superseded_matches ?? 0}</li>
        <li>Team mismatches: {counts.member_side_mismatches ?? 0}</li>
        <li>Unmatched participants: {counts.missing_members_or_unmatched_participants ?? 0}</li>
      </ul>
      {diff.season_changed ? (
        <p className="text-sm text-amber-200">
          Season would change from {displaySeasonLabel(diff.active_season_label) || 'unset'} to{' '}
          {displaySeasonLabel(diff.incoming_season_label) || 'incoming season'}.
        </p>
      ) : null}
      {diff.edition_changed ? (
        <p className="text-sm text-amber-200" data-testid="neonsportz-edition-changed">
          {diff.active_edition
            ? `Edition would change from ${formatEditionDisplay(diff.active_edition)} to ${formatEditionDisplay(diff.incoming_edition) || 'incoming edition'}.`
            : `This import records ${formatEditionDisplay(diff.incoming_edition) || 'a new Madden year'} for the first time.`}
        </p>
      ) : null}
      {diff.stage_changed ? (
        <p className="text-sm text-amber-200">
          Active stage would change from {diff.active_stage_label || 'unset'} to{' '}
          {diff.incoming_stage_label || 'incoming stage'}.
        </p>
      ) : null}
      {diff.destructive_or_review_required_changes.length > 0 ? (
        <ul className="list-disc list-inside text-sm text-amber-200">
          {diff.destructive_or_review_required_changes.map((item) => (
            <li key={item}>{item.replace(/_/g, ' ')}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

const NeonSportzUrlImportPanel: React.FC<NeonSportzUrlImportPanelProps> = ({
  leagueId,
  onImportSuccess,
}) => {
  const [url, setUrl] = useState('');
  const [publishedSeasons, setPublishedSeasons] = useState<LscNeonsportzPublishedSeason[]>([]);
  const [seasonIndex, setSeasonIndex] = useState('');
  const [applyMatches, setApplyMatches] = useState(true);
  const [confirmSeasonRollover, setConfirmSeasonRollover] = useState(false);
  const [confirmEditionRollover, setConfirmEditionRollover] = useState(false);
  const [confirmEditionUndeclared, setConfirmEditionUndeclared] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [importProgress, setImportProgress] = useState<string | null>(null);
  const [error, setError] = useState<ParsedLscMutationError | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getLscScheduleSource(leagueId)
      .then((data) => {
        if (cancelled) return;
        const savedUrl = data.schedule_source?.source_url;
        if (savedUrl) {
          setUrl(savedUrl);
        }
      })
      .catch(() => {
        /* prefill is best-effort */
      });
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  useEffect(() => {
    const trimmed = url.trim();
    if (!trimmed) {
      setPublishedSeasons([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void listLscNeonsportzPublishedSeasons(leagueId, trimmed)
        .then((data) => {
          if (cancelled) return;
          const seasons = data.seasons || [];
          setPublishedSeasons(seasons);
          setSeasonIndex((current) => {
            if (current && seasons.some((row) => String(row.season_index) === current)) {
              return current;
            }
            const currentRow = seasons.find((row) => row.current) || seasons[0];
            return currentRow ? String(currentRow.season_index) : '';
          });
        })
        .catch(() => {
          if (!cancelled) setPublishedSeasons([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [leagueId, url]);
  const [errorIssues, setErrorIssues] = useState<string[]>([]);
  const [result, setResult] = useState<LscNeonsportzUrlPreviewResult | null>(null);
  const [confirmResult, setConfirmResult] = useState<LscNeonsportzUrlConfirmResult | null>(null);

  const selectedSeasonIndex = useMemo(() => {
    if (seasonIndex === '') return null;
    const parsed = Number(seasonIndex);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
  }, [seasonIndex]);

  const selectedPublishedSeason = useMemo(
    () => publishedSeasons.find((row) => row.season_index === selectedSeasonIndex) || null,
    [publishedSeasons, selectedSeasonIndex]
  );

  // The active playable week/stage is taken from NeonSportz automatically; the
  // commissioner no longer chooses it. Shown read-only for confirmation.
  const providerCurrentWeek = useMemo(() => {
    if (result?.summary?.current_stage) return result.summary.current_stage;
    if (result?.league?.week != null) return `Week ${result.league.week}`;
    return '';
  }, [result]);

  const handlePreview = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) {
      setError({
        message: NEONSPORTZ_IMPORT_ERROR_MESSAGES.invalid_neonsportz_url,
        showBillingLink: false,
        showPricingLink: false,
      });
      setErrorIssues([]);
      setResult(null);
      setConfirmResult(null);
      return;
    }

    setLoading(true);
    setError(null);
    setErrorIssues([]);
    setResult(null);
    setConfirmResult(null);

    try {
      const data = await previewLscNeonsportzUrl(
        leagueId,
        trimmed,
        undefined,
        selectedSeasonIndex
      );
      setResult(data);
      setConfirmSeasonRollover(false);
      setConfirmEditionRollover(false);
    } catch (err) {
      if (err instanceof LscApiError) {
        if (err.code && isLscEntitlementErrorCode(err.code)) {
          setError(parseLscMutationError(err, 'Could not preview NeonSportz league.'));
        } else {
          setError({
            message: formatNeonsportzImportError(err.message, err.code),
            showBillingLink: false,
            showPricingLink: false,
          });
        }
        setErrorIssues(formatImportIssues(err.issues));
      } else {
        setError({
          message: 'Could not preview NeonSportz league.',
          showBillingLink: false,
          showPricingLink: false,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    const trimmed = url.trim();
    if (!trimmed || !result) return;

    setConfirming(true);
    setImportProgress('Starting import…');
    setError(null);
    setErrorIssues([]);
    setConfirmResult(null);

    try {
      const data = await confirmLscNeonsportzUrlAndWait(
        leagueId,
        {
          url: trimmed,
          ...(selectedSeasonIndex != null ? { season_index: selectedSeasonIndex } : {}),
          apply_matches: applyMatches,
          confirm_season_rollover: confirmSeasonRollover,
          confirm_edition_rollover: confirmEditionRollover,
          confirm_edition_undeclared: confirmEditionUndeclared,
        },
        {
          onStatus: (status) => {
            if (status === 'queued') setImportProgress('Import queued…');
            else if (status === 'running') setImportProgress('Importing schedule…');
            else setImportProgress('Finishing…');
          },
        }
      );
      setConfirmResult(data);
      setImportProgress(null);
      if (applyMatches) {
        onImportSuccess?.();
      }
    } catch (err) {
      setImportProgress(null);
      if (err instanceof LscApiError) {
        if (err.code && isLscEntitlementErrorCode(err.code)) {
          setError(parseLscMutationError(err, 'Could not import NeonSportz league.'));
        } else if (err.code === 'import_in_progress') {
          setError({
            message: err.message || 'An import is already running for this league.',
            showBillingLink: false,
            showPricingLink: false,
          });
        } else {
          setError({
            message: formatNeonsportzImportError(err.message, err.code),
            showBillingLink: false,
            showPricingLink: false,
          });
        }
        setErrorIssues(formatImportIssues(err.issues));
      } else {
        setError({
          message: 'Could not import NeonSportz league.',
          showBillingLink: false,
          showPricingLink: false,
        });
      }
    } finally {
      setConfirming(false);
    }
  };

  const participants = result?.participants_preview || [];
  const matches = result?.matches_preview || [];
  const truncationNote = formatTruncationNote(result?.counts);
  const scheduleIncomplete = isNeonsportzScheduleIncomplete(result?.counts);
  const weekRange = formatScheduleWeekRange(result?.counts);
  const seasonRolloverRequired = Boolean(result?.diff_summary?.season_changed);
  const editionRolloverRequired = Boolean(result?.diff_summary?.edition_changed);
  const editionUndeclaredRequired = Boolean(result?.diff_summary?.edition_undeclared);
  const teamMismatchCount = result?.diff_summary?.counts?.member_side_mismatches ?? 0;
  const teamAssignmentReviewRequired = Boolean(
    teamMismatchCount > 0 ||
      (result?.diff_summary?.destructive_or_review_required_changes ?? []).includes(
        'member_side_mismatches'
      )
  );
  const confirmSeasonDisplay = formatSeasonWithEdition(
    selectedPublishedSeason?.stored_label || result?.summary?.season_label || result?.diff_summary?.incoming_season_label,
    result?.diff_summary?.incoming_edition || null,
    selectedSeasonIndex ?? result?.counts?.season_index
  );
  const noPublishedSchedule = previewHasNoGames(result?.counts);
  const confirmBlocked =
    noPublishedSchedule ||
    scheduleIncomplete ||
    (applyMatches && seasonRolloverRequired && !confirmSeasonRollover) ||
    (applyMatches && editionRolloverRequired && !confirmEditionRollover) ||
    (applyMatches && editionUndeclaredRequired && !confirmEditionUndeclared) ||
    (applyMatches && teamAssignmentReviewRequired);

  return (
    <div className="space-y-6" data-testid="neonsportz-url-import-panel">
      <form onSubmit={handlePreview} className="card space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-white mb-1">Refresh from NeonSportz</h2>
          <p className="text-sm text-secondary-400">
            Paste your NeonSportz league link to preview schedule changes against the current LSC
            schedule. Confirm import to refresh canonical matches for Live League Games. This is a
            commissioner-controlled refresh, not continuous automated sync.
          </p>
        </div>

        <div>
          <label
            className="block text-sm font-medium text-secondary-300 mb-1"
            htmlFor="neonsportz-url"
          >
            NeonSportz league URL
          </label>
          <input
            id="neonsportz-url"
            className="input-field"
            type="url"
            placeholder="https://neonsportz.com/leagues/LTL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            data-testid="neonsportz-url-input"
          />
        </div>

        <div>
          <label
            className="block text-sm font-medium text-secondary-300 mb-1"
            htmlFor="neonsportz-season"
          >
            Season
          </label>
          <select
            id="neonsportz-season"
            className="input-field"
            value={seasonIndex}
            onChange={(e) => setSeasonIndex(e.target.value)}
            data-testid="neonsportz-season-label-select"
          >
            <option value="">
              {publishedSeasons.length ? 'Select season' : 'Enter a NeonSportz URL to load seasons'}
            </option>
            {publishedSeasons.map((option) => (
              <option key={option.season_index} value={String(option.season_index)}>
                {`${option.display_label} — ${option.game_count} games${option.current ? ' (current)' : ''}`}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          className="btn-primary"
          disabled={loading}
          data-testid="neonsportz-preview-button"
        >
          {loading ? 'Loading preview...' : 'Preview league'}
        </button>
      </form>

      {error && (
        <div className="space-y-2" data-testid="neonsportz-import-error">
          <LscMutationErrorAlert error={error} onDismiss={() => setError(null)} />
          {errorIssues.length > 0 && (
            <ul className="list-disc list-inside text-sm text-red-200">
              {errorIssues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {result && (
        <div className="card space-y-4" data-testid="neonsportz-preview-result">
          <h3 className="font-semibold text-white">League preview</h3>

          <div className="grid gap-3 sm:grid-cols-2 text-sm text-secondary-300">
            <p>
              League:{' '}
              <span className="text-secondary-100">
                {result.league?.name || result.slug || '—'}
              </span>
            </p>
            <p>
              Slug: <span className="text-secondary-100">{result.slug || '—'}</span>
            </p>
            <p>
              Teams:{' '}
              <span className="text-secondary-100">
                {formatNeonsportzTeamCounts(result.counts)}
              </span>
            </p>
            <p>
              Games:{' '}
              <span className="text-secondary-100">
                {formatNeonsportzGameCounts(result.counts)}
              </span>
            </p>
            {result.counts?.season_index != null && (
              <p>
                Season index:{' '}
                <span className="text-secondary-100">{result.counts.season_index}</span>
              </p>
            )}
            {weekRange && (
              <p>
                Week range: <span className="text-secondary-100">{weekRange}</span>
              </p>
            )}
            {result.counts?.max_week_detected != null && (
              <p>
                Max week:{' '}
                <span className="text-secondary-100">{result.counts.max_week_detected}</span>
              </p>
            )}
            {(((result.snapshot_preview as { edition?: string } | undefined)?.edition) ||
              result.league?.madden_year != null) && (
              <p data-testid="neonsportz-edition-label">
                Edition:{' '}
                <span className="text-secondary-100">
                  {(result.snapshot_preview as { edition?: string } | undefined)?.edition ||
                    (result.league?.madden_year != null
                      ? `madden-${String(result.league.madden_year).slice(-2)}`
                      : '—')}
                </span>
              </p>
            )}
            <p>
              Imported on NeonSportz:{' '}
              <span className="text-secondary-100">
                {result.league?.imported ? 'Yes' : 'No'}
              </span>
            </p>
            {result.league?.last_updated && (
              <p>
                Last updated:{' '}
                <span className="text-secondary-100">{result.league.last_updated}</span>
              </p>
            )}
          </div>

          {result.diff_summary ? <DiffSummarySection diff={result.diff_summary} /> : null}

          {truncationNote && (
            <p
              className={`text-sm ${scheduleIncomplete ? 'text-red-200' : 'text-amber-200'}`}
              data-testid="neonsportz-truncation-note"
            >
              {truncationNote}
            </p>
          )}

          {result.warnings && result.warnings.length > 0 && (
            <div>
              <p className="text-sm font-medium text-amber-300 mb-1">Warnings</p>
              <ul className="list-disc list-inside text-sm text-secondary-400">
                {formatImportIssues(result.warnings).map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          {participants.length > 0 && (
            <div>
              <p className="text-sm font-medium text-secondary-200 mb-2">Teams preview</p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left text-secondary-300">
                  <thead>
                    <tr className="text-secondary-400 border-b border-secondary-700">
                      <th className="py-2 pr-4">Team</th>
                      <th className="py-2 pr-4">Discord / NeonSportz member</th>
                      <th className="py-2 pr-4">Discord ID status</th>
                      <th className="py-2 pr-4">In-game identity</th>
                      <th className="py-2 pr-4">Twitch</th>
                      <th className="py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((row, index) => (
                      <tr key={`${row.side_label}-${index}`} className="border-b border-secondary-800">
                        <td className="py-2 pr-4">{row.side_label || '—'}</td>
                        <td className="py-2 pr-4">
                          {formatDiscordMemberLabel(row.discord_username, row.discord_id)}
                        </td>
                        <td className="py-2 pr-4">
                          {formatDiscordIdStatus(row.discord_id_status)}
                        </td>
                        <td className="py-2 pr-4">{row.in_game_name || '—'}</td>
                        <td className="py-2 pr-4">{formatTwitchLogin(row.twitch_login)}</td>
                        <td className="py-2">{row.status || 'preview'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {matches.length > 0 && (
            <div>
              <p className="text-sm font-medium text-secondary-200 mb-2">Schedule preview</p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left text-secondary-300">
                  <thead>
                    <tr className="text-secondary-400 border-b border-secondary-700">
                      <th className="py-2 pr-4">Week/stage</th>
                      <th className="py-2 pr-4">Home</th>
                      <th className="py-2 pr-4">Away</th>
                      <th className="py-2 pr-4">Score/status</th>
                      <th className="py-2">Scheduled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matches.slice(0, 25).map((row, index) => (
                      <tr
                        key={`${row.external_match_id || row.home_side_label}-${index}`}
                        className="border-b border-secondary-800"
                      >
                        <td className="py-2 pr-4">{row.stage_label || '—'}</td>
                        <td className="py-2 pr-4">{row.home_side_label || '—'}</td>
                        <td className="py-2 pr-4">{row.away_side_label || '—'}</td>
                        <td className="py-2 pr-4">
                          {row.home_score != null && row.away_score != null
                            ? `${row.home_score}-${row.away_score}`
                            : row.status || '—'}
                        </td>
                        <td className="py-2">{row.scheduled_at || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {matches.length > 25 && (
                <p className="text-xs text-secondary-500 mt-2">
                  Showing first {result.counts?.preview_displayed_games ?? 25} of{' '}
                  {result.counts?.fetched_games ?? matches.length} fetched games.
                </p>
              )}
            </div>
          )}

          <div className="border-t border-secondary-800 pt-4 space-y-4" data-testid="neonsportz-confirm-panel">
            <p className="text-sm text-secondary-300" data-testid="neonsportz-confirm-safety-note">
              This will import the schedule into Stream Center. It will not create member accounts or
              infer Twitch logins.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-secondary-300 mb-1">
                  Season label
                </label>
                <p
                  className="input-field bg-secondary-900/40 text-secondary-100 cursor-default"
                  data-testid="neonsportz-confirm-season-label"
                >
                  {confirmSeasonDisplay || 'Selected above'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-300 mb-1">
                  NeonSportz current week
                </label>
                <p
                  className="input-field bg-secondary-900/40 text-secondary-100 cursor-default"
                  data-testid="neonsportz-provider-current-week"
                >
                  {providerCurrentWeek || 'Detected from NeonSportz'}
                </p>
                <p className="text-xs text-secondary-500 mt-1">
                  LSC uses NeonSportz's current week automatically. No phase selection needed.
                </p>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-secondary-300">
              <input
                type="checkbox"
                checked={applyMatches}
                onChange={(e) => setApplyMatches(e.target.checked)}
              />
              Apply matches after import
            </label>

            {seasonRolloverRequired ? (
              <div
                className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 space-y-2"
                data-testid="neonsportz-season-rollover-ack"
              >
                <p className="text-sm text-amber-100">
                  New season schedule detected. Review the preview above before applying.
                </p>
                <label className="flex items-start gap-2 text-sm text-amber-100">
                  <input
                    type="checkbox"
                    checked={confirmSeasonRollover}
                    disabled={noPublishedSchedule}
                    onChange={(e) => setConfirmSeasonRollover(e.target.checked)}
                    data-testid="neonsportz-season-rollover-checkbox"
                  />
                  {SCHEDULE_IMPORT_ROLLOVER_ACK_COPY}
                </label>
              </div>
            ) : null}

            {editionRolloverRequired ? (
              <div
                className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 space-y-2"
                data-testid="neonsportz-edition-rollover-ack"
              >
                <p className="text-sm text-amber-100">{editionRolloverAckCopy(result?.diff_summary)}</p>
                <label className="flex items-start gap-2 text-sm text-amber-100">
                  <input
                    type="checkbox"
                    checked={confirmEditionRollover}
                    onChange={(e) => setConfirmEditionRollover(e.target.checked)}
                    data-testid="neonsportz-edition-rollover-checkbox"
                  />
                  I confirm this is a new game year
                </label>
              </div>
            ) : null}

            {editionUndeclaredRequired ? (
              <div
                className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 space-y-2"
                data-testid="neonsportz-edition-undeclared-ack"
              >
                <p className="text-sm text-amber-100">
                  This sync didn't say which Madden edition it belongs to. Click Preview league and see
                  whether the edition appears this time. If it does, click Import this league. If it still
                  doesn't, wait and try again later — forcing it with the checkbox imports games that
                  won't appear on League Home and won't be detected during live streams.
                </p>
                <label className="flex items-start gap-2 text-sm text-amber-100">
                  <input
                    type="checkbox"
                    checked={confirmEditionUndeclared}
                    onChange={(e) => setConfirmEditionUndeclared(e.target.checked)}
                    data-testid="neonsportz-edition-undeclared-checkbox"
                  />
                  Import without an edition. These games won't appear on League Home and won't be
                  detected during live streams.
                </label>
              </div>
            ) : null}

            {teamAssignmentReviewRequired ? (
              <p className="text-sm text-amber-100" data-testid="neonsportz-team-assignment-review-note">
                {teamAssignmentNextStepCopy(teamMismatchCount)}{' '}
                <Link
                  to={lscPath(`/leagues/${leagueId}/members`)}
                  className="text-primary-300 hover:text-primary-200 underline"
                >
                  Review team assignments
                </Link>
              </p>
            ) : null}

            <button
              type="button"
              className="btn-primary"
              disabled={confirming || confirmBlocked}
              onClick={() => void handleConfirm()}
              data-testid="neonsportz-confirm-button"
            >
              {confirming ? importProgress || 'Importing…' : 'Import this league'}
            </button>
            {noPublishedSchedule ? (
              <p className="text-sm text-red-200" data-testid="neonsportz-empty-schedule-note">
                {NEONSPORTZ_EMPTY_SCHEDULE_COPY}
              </p>
            ) : null}
            {scheduleIncomplete && !noPublishedSchedule && truncationNote ? (
              <p className="text-sm text-red-200" data-testid="neonsportz-import-blocked-note">
                {truncationNote}
              </p>
            ) : null}
            {applyMatches && seasonRolloverRequired && !confirmSeasonRollover ? (
              <p className="text-sm text-amber-200" data-testid="neonsportz-rollover-confirm-required">
                Confirm the new season review checkbox before applying.
              </p>
            ) : null}
          </div>
        </div>
      )}

      {confirmResult && (
        <div className="card space-y-3" data-testid="neonsportz-confirm-result">
          <h3 className="font-semibold text-white">Import result</h3>
          <p className="text-sm text-secondary-300">
            Snapshot saved. Matches applied:{' '}
            <span className="text-secondary-100">
              {confirmResult.apply?.applied_matches ?? confirmResult.apply?.matches_applied ?? 0}
            </span>
          </p>
          {confirmResult.summary?.current_stage ? (
            <p className="text-sm text-secondary-300" data-testid="neonsportz-lsc-current-week">
              LSC current week:{' '}
              <span className="text-secondary-100">{confirmResult.summary.current_stage}</span>
            </p>
          ) : null}
          {confirmResult.warnings && confirmResult.warnings.length > 0 && (
            <ul className="list-disc list-inside text-sm text-secondary-400">
              {formatImportIssues(confirmResult.warnings).map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}
          <Link
            to={lscPath(`/leagues/${leagueId}/matches`)}
            className="btn-secondary inline-block"
            data-testid="neonsportz-view-matches-link"
          >
            View league schedules
          </Link>
          <ImportedMembersPromotionPanel
            leagueId={leagueId}
            snapshotId={confirmResult.snapshot_id}
          />
        </div>
      )}

      <p className="text-sm text-secondary-400" data-testid="neonsportz-csv-fallback-note">
        Having trouble? Upload NeonSportz Teams + Games CSV instead (see below).
      </p>
    </div>
  );
};

export default NeonSportzUrlImportPanel;
